import { formatDateLocal, formatDateTimeLocal } from "@/shared/lib/date-time";
import { labelServiceStage } from "../lib/trainingLaoLabels";
import type { TrainingServiceStandardDetailModel } from "./TrainingServiceStandardDetailView";

type Props = { standard: TrainingServiceStandardDetailModel };

export function TrainingServiceStandardPdfPreviewContent({ standard }: Props) {
  const doItems = standard.items.filter((i) => i.itemType === "do");
  const dontItems = standard.items.filter((i) => i.itemType === "dont");

  return (
    <div
      id="service-standard-pdf-content"
      data-tourid="training-standard-pdf-content"
      className="mx-auto max-w-4xl bg-white p-8 font-serif text-gray-900 leading-relaxed print:max-w-none print:p-0"
      style={{
        fontFamily: '"Times New Roman", Times, serif',
        lineHeight: "1.5",
        color: "#1a1a1a",
      }}
    >
      <div className="mb-8 border-gray-800 border-b-4 pb-6">
        <div className="mb-6 text-center">
          <h1 className="font-bold text-4xl text-gray-800 uppercase tracking-wider">
            FREEDOM Sole Co.Ltd
          </h1>
          <p className="mt-2 text-gray-600 text-lg">ມາດຕະຖານບໍລິການ</p>
          <div className="mx-auto mt-4 h-1 w-24 bg-gray-800" />
        </div>
        <div className="flex items-start justify-between text-sm">
          <p className="font-semibold">
            ເອກະສານ:{" "}
            <span className="font-normal">{`STD-${standard.id.slice(-6).toUpperCase()}`}</span>
          </p>
          <div className="text-right">
            <p className="font-semibold">ວັນທີພິມ</p>
            <p className="font-normal">{formatDateLocal(new Date())}</p>
          </div>
        </div>
      </div>

      <h2 className="mb-6 border-gray-300 border-b-2 pb-4 text-center font-bold text-2xl text-gray-800">
        {standard.name}
      </h2>

      <div className="mb-8">
        <h3 className="mb-3 font-bold text-gray-800 text-lg">ຂໍ້ມູນພື້ນຖານ</h3>
        <table className="w-full border-collapse border-2 border-gray-400 text-sm">
          <tbody>
            <tr className="border-gray-300 border-b">
              <td className="w-1/3 border-gray-300 border-r bg-gray-100 p-3 font-semibold">
                ຊ່ອງທາງ
              </td>
              <td className="p-3">{standard.serviceChannelName || "—"}</td>
            </tr>
            <tr className="border-gray-300 border-b">
              <td className="border-gray-300 border-r bg-gray-100 p-3 font-semibold">ແຜນກ</td>
              <td className="p-3">{standard.departmentName || "—"}</td>
            </tr>
            <tr className="border-gray-300 border-b">
              <td className="border-gray-300 border-r bg-gray-100 p-3 font-semibold">ຕໍາແໜ່ງ</td>
              <td className="p-3">{standard.positionTitle || "ທຸກຕໍາແໜ່ງໃນແຜນກ"}</td>
            </tr>
            <tr className="border-gray-300 border-b">
              <td className="border-gray-300 border-r bg-gray-100 p-3 font-semibold">ໃຊ້ເວລາ</td>
              <td className="p-3">{labelServiceStage(standard.serviceStage)}</td>
            </tr>
            <tr>
              <td className="border-gray-300 border-r bg-gray-100 p-3 font-semibold">
                ອັບເດດລ່າສຸດ
              </td>
              <td className="p-3">{formatDateTimeLocal(standard.updatedAt)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {standard.description ? (
        <div className="mb-8">
          <h3 className="mb-2 font-bold text-gray-800 text-lg">ຄຳອະທິບາຍ</h3>
          <p className="whitespace-pre-line text-sm">{standard.description}</p>
        </div>
      ) : null}

      <div className="mb-8">
        <h3 className="mb-3 font-bold text-green-800 text-lg">ສິ່ງທີ່ຕ້ອງເຮັດ</h3>
        <ol className="list-decimal space-y-2 pl-6 text-sm">
          {doItems.map((item) => (
            <li key={item.id}>
              <span className="font-medium">{item.content}</span>
              {item.description ? (
                <span className="block text-gray-600">{item.description}</span>
              ) : null}
            </li>
          ))}
        </ol>
        {doItems.length === 0 ? <p className="text-gray-500 text-sm">—</p> : null}
      </div>

      <div className="mb-8">
        <h3 className="mb-3 font-bold text-red-800 text-lg">ສິ່ງທີ່ຫ້າມເຮັດ</h3>
        <ol className="list-decimal space-y-2 pl-6 text-sm">
          {dontItems.map((item) => (
            <li key={item.id}>
              <span className="font-medium">{item.content}</span>
              {item.description ? (
                <span className="block text-gray-600">{item.description}</span>
              ) : null}
            </li>
          ))}
        </ol>
        {dontItems.length === 0 ? <p className="text-gray-500 text-sm">—</p> : null}
      </div>

      {standard.examples.length > 0 ? (
        <div className="mb-8">
          <h3 className="mb-3 font-bold text-gray-800 text-lg">ຕົວຢ່າງປະໂຫຍກ</h3>
          <div className="space-y-3 text-sm">
            {standard.examples.map((ex) => (
              <div key={ex.id} className="border-gray-300 border-l-4 pl-3">
                <p className="font-semibold">{ex.situation}</p>
                <p>{ex.example}</p>
                {ex.description ? <p className="text-gray-600">{ex.description}</p> : null}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {standard.specialCases.length > 0 ? (
        <div className="mb-8">
          <h3 className="mb-3 font-bold text-gray-800 text-lg">ກໍລະນີພິເສດ</h3>
          <div className="space-y-3 text-sm">
            {standard.specialCases.map((sc) => (
              <div key={sc.id} className="border-gray-300 border-l-4 pl-3">
                <p className="font-semibold">{sc.title}</p>
                <p>{sc.handling}</p>
                {sc.description ? <p className="text-gray-600">{sc.description}</p> : null}
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
